import { Field, FieldLabel } from '@plato/ui/primitives/field';
import { Typography } from '@plato/ui/primitives/typography';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
// No @plato/ui radio, select or link yet, so these stay on the older webapp set.
import { RadioList, RadioListItem } from '@plato/ui-legacy/components/ui/button-radio-list';
import { LinkText } from '@plato/ui-legacy/components/ui/link-text';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectList,
    SelectTrigger,
    SelectValue,
    type SelectOption,
} from '@plato/ui/components/select';
import { Dialog } from '@plato/ui/containers/dialog';
// No @plato/ui radio or link yet, so these stay on the older webapp set.
import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { DialogForm } from '@plato/ui-legacy/components/dialog/dialog-form';
import { DialogStateManager, useDialog } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { plainTextToLexicalSync } from '@plato/ui-legacy/lib/rich-text/plain-text-to-lexical';
import type { RouterOutput } from '../../../server/types/infer-trpc-types.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { useHiddenSuggestions } from '../hooks/use-hidden-suggestions.ts';

type Suggestion = RouterOutput['platoUser']['getManyCustomers']['getCustomersWithoutEvents'][number];

export type SuggestionFeedbackDialogProps = {
    customer: Suggestion;
    onHidden?: () => void;
};

type Reason = 'set_inactive' | 'never_visited' | 'too_frequent';

const REASONS: Reason[] = ['set_inactive', 'never_visited', 'too_frequent'];

// The options used to be two levels deep, and PREP-998 recorded that shape as reason + sub_option.
// Flattening the UI keeps the emitted pairs so the dismissal series doesn't fork at this release.
const ANALYTICS_BY_REASON: Record<Reason, { reason: string; sub_option?: string }> = {
    set_inactive: { reason: 'not_visited', sub_option: 'set_inactive' },
    never_visited: { reason: 'not_visited', sub_option: 'do_not_visit' },
    too_frequent: { reason: 'too_frequent' },
};

const FREQUENCY_OPTIONS = [2, 4, 8, 12];

function SuggestionFeedbackDialogInternal({ customer, onHidden }: SuggestionFeedbackDialogProps): ReactNode {
    const { t } = useTranslation('events');
    const { visible, remove } = useDialog();
    const { toast } = useToast();
    const sendEvent = useAnalytics();
    const { hide, unhide } = useHiddenSuggestions();
    const { canMarkCustomerInactive } = useAppConfig();
    const apiUtils = api.useUtils();

    const [reason, setReason] = useState<Reason | null>(null);
    const [frequencyWeeks, setFrequencyWeeks] = useState<number>(4);
    const frequencySelectId = useId();

    const clientHide = (): void => {
        hide(customer.customerId);
        const hiddenToast = toast({
            icon: 'success',
            title: t('suggestion_feedback__toast_hidden'),
            description: (
                <LinkText
                    as="button"
                    onClick={() => {
                        undoDismiss();
                        hiddenToast.dismiss();
                    }}
                >
                    {t('form__undo', { ns: 'common' })}
                </LinkText>
            ),
        });
        onHidden?.();
        remove();
    };

    const { mutate: updateVisitPreferences, isPending: isSavingPreferences } =
        api.platoUser.command.customers.updateVisitPreferences.useMutation({
            onError: () => {
                toast({ icon: 'warning', title: t('suggestion_feedback__error') });
            },
            onSuccess: (_data, variables) => {
                // Undo: nothing to log, nothing to hide.
                if (variables.isVisited === true) {
                    return;
                }

                void apiUtils.platoUser.query.customers.getCustomersWithoutEvents.invalidate();

                sendEvent('event_recommendation_hidden', {
                    customer_id: customer.customerId,
                    ...(variables.isVisited === false
                        ? ANALYTICS_BY_REASON.never_visited
                        : ANALYTICS_BY_REASON.too_frequent),
                    frequency_weeks: variables.visitCadenceInWeeks ?? undefined,
                });

                clientHide();
            },
        });

    // Unhiding alone would leave the customer suppressed in the database.
    const undoDismiss = (): void => {
        unhide(customer.customerId);
        updateVisitPreferences({
            customerId: customer.customerId,
            isVisited: true,
            visitCadenceInWeeks: null,
        });
    };

    const { mutate: updateStatus, isPending } =
        api.platoUser.command.customers.updateBusinessStatus.useMutation({
            onError: () => {
                toast({ icon: 'warning', title: t('suggestion_feedback__error') });
            },
            onSuccess: async () => {
                await Promise.all([
                    apiUtils.platoUser.query.customers.getCustomersWithoutEvents.invalidate(),
                    apiUtils.platoUser.getDetailCustomer.getBasics.invalidate({
                        erpCustomerId: customer.customerId,
                    }),
                    apiUtils.platoUser.getDetailCustomer.getCrmDetail.invalidate({
                        erpCustomerId: customer.customerId,
                    }),
                    apiUtils.platoUser.getDetailCustomer.getLatestStatusChange.invalidate({
                        erpCustomerId: customer.customerId,
                    }),
                    apiUtils.platoUser.getManyCustomers.getListWithFilters.invalidate(),
                    apiUtils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate(),
                ]);

                // Fired here, not in handleConfirm: this is the one branch that can fail, and a
                // retry after failure would otherwise double-count the same dismissal.
                sendEvent('event_recommendation_hidden', {
                    customer_id: customer.customerId,
                    ...ANALYTICS_BY_REASON.set_inactive,
                });
                sendEvent('customer_marked_inactive', {
                    customer_id: customer.customerId,
                    reason: t('suggestion_feedback__inactive_reason'),
                });

                toast({ icon: 'success', title: t('suggestion_feedback__toast_inactive') });
                onHidden?.();
                remove();
            },
        });

    const addressText = [customer.address.zipCode, customer.address.city].filter(Boolean).join(' ');
    const tierLabelMap: Record<string, string> = {
        A: t('recommended_customers_tier_a'),
        B: t('recommended_customers_tier_b'),
        C: t('recommended_customers_tier_c'),
    };
    const tierLabel = customer.tier ? tierLabelMap[customer.tier] : undefined;
    const headerParts = [customer.customerName, addressText, tierLabel].filter(Boolean).join(' · ');

    const availableReasons = REASONS.filter((r) => r !== 'set_inactive' || canMarkCustomerInactive);

    const getReasonLabel = (value: Reason): string => {
        switch (value) {
            case 'never_visited':
                return t('suggestion_feedback__reason_never_visited');
            case 'set_inactive':
                return t('suggestion_feedback__reason_set_inactive');
            case 'too_frequent':
                return t('suggestion_feedback__reason_too_frequent');
        }
    };

    const frequencies: SelectOption[] = FREQUENCY_OPTIONS.map((weeks) => ({
        value: String(weeks),
        label: t('suggestion_feedback__frequency_weeks', { weeks }),
    }));

    const isInactivePath = reason === 'set_inactive';

    const handleConfirm = (): void => {
        if (reason === null) {
            return;
        }

        if (reason === 'set_inactive') {
            updateStatus({
                customerId: customer.customerId,
                businessStatus: 'inactive',
                reason: plainTextToLexicalSync(t('suggestion_feedback__inactive_reason')),
            });
            return;
        }

        if (reason === 'never_visited') {
            updateVisitPreferences({ customerId: customer.customerId, isVisited: false });
            return;
        }

        updateVisitPreferences({ customerId: customer.customerId, visitCadenceInWeeks: frequencyWeeks });
    };

    return (
        <Dialog open={visible} onClose={remove}>
            <DialogForm className="sm:max-w-md">
                <DialogForm.Header title={t('suggestion_feedback__title')} subtitle={headerParts} />
                <DialogForm.Content className="gap-default py-relaxed">
                    <RadioList
                        value={reason ?? undefined}
                        onValueChange={(value) => {
                            setReason(availableReasons.find((r) => r === value) ?? null);
                        }}
                        className="gap-default"
                    >
                        {availableReasons.map((r) => (
                            <div key={r} className="flex flex-col gap-cozy">
                                <RadioListItem value={r} label={getReasonLabel(r)} />

                                {r === 'set_inactive' && (
                                    /* pl-6 is optical: aligns past the 16px dot + 8px gap. */
                                    <Typography variant="caption" className="pl-6 text-content-weaker">
                                        {t('suggestion_feedback__reason_set_inactive_hint')}
                                    </Typography>
                                )}

                                {reason === r && r === 'too_frequent' && (
                                    <Field className="pl-relaxed">
                                        <FieldLabel htmlFor={frequencySelectId}>
                                            {t('suggestion_feedback__frequency_label')}
                                        </FieldLabel>
                                        <Select
                                            title={t('suggestion_feedback__frequency_label')}
                                            items={frequencies}
                                            value={String(frequencyWeeks)}
                                            onValueChange={(frequency) => {
                                                if (frequency) {
                                                    setFrequencyWeeks(Number(frequency));
                                                }
                                            }}
                                        >
                                            <SelectTrigger id={frequencySelectId} className="w-full">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectList>
                                                    {(frequency: SelectOption) => (
                                                        <SelectItem key={frequency.value} value={frequency} />
                                                    )}
                                                </SelectList>
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                )}
                            </div>
                        ))}
                    </RadioList>
                </DialogForm.Content>
                <DialogForm.SubmitBlock
                    onCancel={remove}
                    onSubmit={handleConfirm}
                    isLoading={isPending || isSavingPreferences}
                    disabled={reason === null}
                    submitText={
                        isInactivePath
                            ? t('suggestion_feedback__submit_inactive')
                            : t('suggestion_feedback__submit')
                    }
                    variant={isInactivePath ? 'danger' : 'primary'}
                />
            </DialogForm>
        </Dialog>
    );
}

export const SuggestionFeedbackDialog = DialogStateManager.create<SuggestionFeedbackDialogProps>(
    (props: SuggestionFeedbackDialogProps): ReactNode => <SuggestionFeedbackDialogInternal {...props} />,
);
